import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Spinner, Table } from "reactstrap";
import moment from "moment";

import { useDateFilter } from "hooks/datefilter";
import PageHero, { PageHeroExportButton, PageHeroRefreshButton } from "components/PageHero";
import MetricCard from "components/MetricCard";
import MetricCardGroup from "components/MetricCardGroup";
import ShellCard, { TableSurface } from "components/ShellCard";
import SectionCard from "components/SectionCard";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { downloadBlob } from "utils/downloadBlob";
import { formatDocument } from "utils/formatDoc";
import { obterFunilRede, baixarFunilRedeXlsx } from "services/onboardingTransportadoraAdminApi";

import { BLOCOS, DIAS_PADRAO, diasDesde, etapasDoBloco, fmtDias, fmtNumero, linkCuradoria, ordenarEtapas, rotuloEtapa } from "./regras";

// Everlog Admin › Transportadoras › Funil da Rede (PO1-3649, épico PO1-3359):
// quantas transportadoras estão em cada etapa da Rede Everlog (pedido →
// curadoria → aceite → habilitação → cobertura/tabela/API → aparições →
// leilão/BID → vinculadas por embarcador real), quem está parado em cada uma e
// o tempo médio entre as etapas. Molde de Trackfy › Indicadores › Despacho:
// PageHero + MetricCardGroup (cards que abrem a lista de parados da etapa) +
// ShellCard/TableSurface, período pelo filtro global da navbar (o path está em
// DATE_FILTER_ENABLED_PATHS) e exportação em xlsx com downloadBlob.
//
// Dados de GET /onboarding-transportadora/admin/rede/funil?de&ate (backend_hub).

const DATA = "YYYY-MM-DD";
const mutedStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.8rem" };
const stateBoxStyle = { display: "flex", alignItems: "center", gap: "0.6rem", padding: "1rem 0.25rem", color: AUTOPILOT_COLORS.muted, fontSize: "0.9rem" };

export default function RedeEverlogFunil() {
  const { startDate, endDate } = useDateFilter();
  const [etapas, setEtapas] = useState([]);
  const [tempos, setTempos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [erro, setErro] = useState("");
  const [etapaAberta, setEtapaAberta] = useState(null);

  // Sem período no filtro global, os últimos 90 dias.
  const de = (startDate ? moment(startDate) : moment().subtract(DIAS_PADRAO, "days")).format(DATA);
  const ate = (endDate ? moment(endDate) : moment()).format(DATA);
  const periodo = useMemo(() => ({ de, ate }), [de, ate]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro("");
    try {
      const r = await obterFunilRede(periodo);
      setEtapas(ordenarEtapas(r && r.etapas));
      setTempos((r && r.temposMedios) || []);
    } catch (err) {
      const msg = (err.response && err.response.data && err.response.data.error) || "Não conseguimos carregar o funil da Rede agora.";
      setErro(msg);
      setEtapas([]);
      setTempos([]);
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const exportar = useCallback(async () => {
    setExportando(true);
    try {
      const blob = await baixarFunilRedeXlsx(periodo);
      downloadBlob(blob, `funil-rede-everlog-${periodo.de}-a-${periodo.ate}.xlsx`, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
    } catch (_) {
      setErro("Não conseguimos gerar o xlsx agora.");
    } finally {
      setExportando(false);
    }
  }, [periodo]);

  const alternarEtapa = (chave) => setEtapaAberta((atual) => (atual === chave ? null : chave));
  const aberta = etapas.find((e) => e.chave === etapaAberta) || null;
  const periodoLegivel = `${moment(periodo.de).format("DD/MM/YYYY")} a ${moment(periodo.ate).format("DD/MM/YYYY")}`;
  const agora = Date.now();

  return (
    <div className="content rede-funil">
      <PageHero
        eyebrow="Everlog Admin · Transportadoras"
        title="Funil da Rede Everlog"
        description="Quantas transportadoras estão em cada etapa da Rede, quem está parado e quanto tempo leva de uma etapa à outra. O período vem do filtro da barra superior."
        actions={
          <>
            <PageHeroRefreshButton onClick={carregar} loading={loading} />
            <PageHeroExportButton onClick={exportar} loading={exportando} disabled={loading} label="Exportar xlsx" />
          </>
        }
      />

      {erro ? (
        <ShellCard>
          <div style={stateBoxStyle} role="alert">
            {erro}
          </div>
        </ShellCard>
      ) : null}

      {BLOCOS.map((b) => {
        const lista = etapasDoBloco(etapas, b.chave);
        if (!lista.length) return null;
        return (
          <MetricCardGroup
            key={b.chave}
            minTileWidth={170}
            eyebrow={b.titulo}
            title={b.titulo}
            description={`${b.descricao} Clique num indicador para ver quem está parado nele.`}
            action={<span style={{ ...mutedStyle, whiteSpace: "nowrap", fontWeight: 700 }}>{periodoLegivel}</span>}
          >
            {lista.map((e) => (
              <MetricCard
                key={e.chave}
                label={e.rotulo}
                value={loading ? "…" : fmtNumero(e.total)}
                helper={e.parados && e.parados.length ? `${e.parados.length} parado(s)` : undefined}
                iconClassName={e.icone}
                tone={e.tom}
                onClick={() => alternarEtapa(e.chave)}
                active={etapaAberta === e.chave}
              />
            ))}
          </MetricCardGroup>
        );
      })}

      {!loading && !erro && etapas.length === 0 ? (
        <ShellCard>
          <div style={stateBoxStyle}>Sem dados no período.</div>
        </ShellCard>
      ) : null}

      {/* Parados na etapa escolhida: link para a curadoria quando há idConvite. */}
      <ShellCard
        title={aberta ? `Parados em ${aberta.rotulo}` : "Parados por etapa"}
        description={aberta ? `${fmtNumero((aberta.parados || []).length)} transportadora(s) paradas nesta etapa no período.` : "Clique num indicador acima para listar quem está parado nele."}
        action={aberta ? <Button size="sm" color="secondary" outline onClick={() => setEtapaAberta(null)}>Fechar</Button> : null}
      >
        <TableSurface>
          {loading ? (
            <div style={stateBoxStyle}>
              <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /> Consultando o funil...
            </div>
          ) : null}
          {!loading && aberta && (aberta.parados || []).length === 0 ? <div style={stateBoxStyle}>Ninguém parado nesta etapa.</div> : null}
          {!loading && aberta && (aberta.parados || []).length > 0 ? (
            <Table className="rede-funil__parados hub-thead-accent" size="sm" responsive hover>
              <thead className="text-primary">
                <tr>
                  <th>Transportadora</th>
                  <th>CNPJ</th>
                  <th>Desde</th>
                  <th className="text-right">Parada há</th>
                  <th aria-label="Atalhos" />
                </tr>
              </thead>
              <tbody>
                {aberta.parados.map((p, i) => {
                  const link = linkCuradoria(p);
                  const dias = diasDesde(p.desde, agora);
                  return (
                    <tr key={`${p.idConvite || "c"}-${p.idTransportadora || "t"}-${i}`}>
                      <td style={{ color: AUTOPILOT_COLORS.title, fontWeight: 600 }}>{p.nome || "—"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{p.cnpj ? formatDocument(p.cnpj) : "—"}</td>
                      <td style={{ whiteSpace: "nowrap" }}>{p.desde ? moment(p.desde).format("DD/MM/YYYY") : "—"}</td>
                      <td className="text-right" style={{ whiteSpace: "nowrap" }}>{dias === null ? "–" : `${dias} d`}</td>
                      <td className="text-right" style={{ whiteSpace: "nowrap" }}>
                        {link ? (
                          <Link to={link} className="btn btn-sm btn-outline-info" title="Abrir na Curadoria de Transportadoras">
                            Curadoria
                          </Link>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : null}
        </TableSurface>
      </ShellCard>

      {/* Tempos médios entre etapas, em dias. */}
      <ShellCard title="Tempo médio entre etapas" description="Média, no período, do tempo entre uma etapa e a seguinte.">
        <SectionCard variant="field">
          {!loading && tempos.length === 0 ? <div style={stateBoxStyle}>Sem tempos no período.</div> : null}
          {tempos.length > 0 ? (
            <Table className="rede-funil__tempos hub-thead-accent" size="sm" responsive>
              <thead className="text-primary">
                <tr>
                  <th>De</th>
                  <th>Para</th>
                  <th className="text-right">Tempo médio</th>
                </tr>
              </thead>
              <tbody>
                {tempos.map((t, i) => (
                  <tr key={`${t.de}-${t.para}-${i}`}>
                    <td>{rotuloEtapa(t.de)}</td>
                    <td>{rotuloEtapa(t.para)}</td>
                    <td className="text-right" style={{ whiteSpace: "nowrap" }}>{fmtDias(t.dias)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}
        </SectionCard>
      </ShellCard>
    </div>
  );
}
